import React, { useState } from 'react';
import Reveal from 'react-reveal';
import { Helmet } from 'react-helmet';
import Layout from "../components/layouts";
import casualFridaysLogo from "../images/casual-fridays-logo-transparent.png";
import casualFridaysHero from "../images/casual-fridays-hero.jpg";
import texturePaper from "../images/coachella-texture-paper.png"; // used only in Categories section
import cf1 from "../images/casual-fridays-1.png";
import cf2 from "../images/casual-fridays-2.jpg";
import cf3 from "../images/casual-fridays-3.jpg";
import cf4 from "../images/casual-fridays-4.jpg";
import cf5 from "../images/casual-fridays-5.jpg";

// Original dimensions: 1 = 1280×789, 2 = 4163×6245, 3 = 3493×5239
const CF_ASPECT = { 1: 1280 / 789, 2: 4163 / 6245, 3: 3493 / 5239 };
// Split section image: 75% of original height (reduce by 25%)
const CF_ASPECT_SPLIT = 4163 / (0.75 * 6245);

const SHOW_REGISTER = false;
const SHARE_URL = 'https://globalinheritance.org/casual-fridays';
const SHARE_URL_ENCODED = encodeURIComponent(SHARE_URL);
const SHARE_TEXT_ENCODED = encodeURIComponent('Casual Fridays — Sustainable Fashion Challenge at Coachella 2026');

const CasualFridays = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    const url = typeof window !== 'undefined' ? window.location.href : SHARE_URL;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const categories = [
    {
      title: 'Pre-You Vintage',
      description: 'Style a thrifted look from a decade before you were born. This is a styling category. Share a story for each piece and its era.',
    },
    {
      title: 'Planet Friendly',
      description: 'Create an original outfit using sustainable or reclaimed materials. All pieces must be designed or assembled by you.',
    },
    {
      title: 'Saving Receipts',
      description: 'Your best head-to-toe thrifted fit sourced from Coachella Valley thrift stores. Support local stores and show us your best find.',
    },
    {
      title: 'Coachella 2026 Remix',
      description: 'Build a look using pre-loved Coachella or artist merch from the 2026 lineup.',
    },
  ];

  const steps = [
    { num: 1, title: 'Register', text: 'Register your outfit and tell your story by April 15th for Weekend 2' },
    { num: 2, title: 'Get Selected', text: 'Five finalists will be selected per category. All finalists must have a Coachella festival pass.' },
    { num: 3, title: 'Hit the Stage', text: 'Finalists will meet us on Friday at 11:00 AM inside the Desert Sky Tent for the Casual Fridays finals. The competition is open to all Coachella 2026 pass holders.' },
    { num: 4, title: 'Show + Tell', text: 'Share the story behind your look with the audience and judges.' },
    { num: 5, title: 'The Big Announcement', text: 'Prizes include: Artist Guest Passes, VIP upgrades, Festival restaurant vouchers + more' },
  ];

  return (
    <Layout navBg>
      <Helmet>
        <title>Casual Fridays | Global Inheritance</title>
        <meta name="description" content="Sustainable Fashion Challenge at Coachella 2026 — Show Us What You Go. More details coming soon." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
        <link href="https://fonts.cdnfonts.com/css/sweet-sans-pro" rel="stylesheet" />
      </Helmet>
      <div
        className="casual-fridays-page"
        style={{ ['--cf-texture-paper' as string]: `url(${texturePaper})` }}
      >
        {/* Hero: stage photo with logo over a faded gradient */}
        <header
          className="casual-fridays-hero"
          style={{ backgroundImage: `url(${casualFridaysHero})` }}
        >
          <div className="casual-fridays-hero-content">
            <Reveal effect="animated fadeInUp" duration={1200}>
              <img className="casual-fridays-logo" src={casualFridaysLogo} alt="Casual Fridays" />
            </Reveal>
          </div>
        </header>

        {/* What is Casual Fridays? — copy + CTA tile + image */}
        <section className="casual-fridays-section casual-fridays-section--split">
          <div className="casual-fridays-section-inner">
            <Reveal effect="animated fadeInLeft" duration={1200}>
              <h2 className="casual-fridays-heading">What is Casual Fridays?</h2>
            </Reveal>
            <Reveal effect="animated fadeInLeft" delay={300} duration={1200}>
              <p>Coachella style is rooted in self-expression. This year we're spotlighting looks that are thrifted, remixed, or sustainably made. Choose a category, build your outfit, and show us how you made it. Selected participants will present their looks live for judges and a festival audience. Special guest judges to be announced.</p>
            </Reveal>
            <Reveal effect="animated fadeInLeft" delay={450} duration={1200}>
              <div className="casual-fridays-cta-tile">
                <p className="casual-fridays-cta-tile-text">More details coming soon.</p>
              </div>
            </Reveal>
          </div>
          <Reveal effect="animated fadeInRight" delay={200} duration={1200}>
            <div className="casual-fridays-section-media" style={{ aspectRatio: String(CF_ASPECT_SPLIT) }}>
              <img src={cf2} alt="Sustainable style at the festival" />
            </div>
          </Reveal>
        </section>

        {/* Categories — intro + grid with placeholder */}
        <section className="casual-fridays-section casual-fridays-section--dark">
          <div className="casual-fridays-section-inner casual-fridays-section-inner--wide">
            <Reveal effect="animated fadeIn" duration={1200}>
              <h2 className="casual-fridays-heading casual-fridays-heading--center">Categories</h2>
            </Reveal>
            <Reveal effect="animated fadeIn" delay={100} duration={1200}>
              <div className="casual-fridays-category-grid">
                {categories.map((cat, i) => (
                  <div key={cat.title} className="casual-fridays-category-card">
                    <Reveal effect="animated fadeInUp" delay={i * 80} duration={1000}>
                      <div className="casual-fridays-category-card-inner">
                        <h3>{cat.title}</h3>
                        <p>{cat.description}</p>
                      </div>
                    </Reveal>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal effect="animated fadeIn" delay={400} duration={1200}>
              <div className="casual-fridays-categories-visual" style={{ aspectRatio: String(CF_ASPECT[1]) }}>
                <img src={cf1} alt="Category inspiration" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Next Steps — alternating copy + image */}
        <section id="register" className="casual-fridays-section casual-fridays-section--steps">
          <div className="casual-fridays-section-inner casual-fridays-section-inner--wide">
            <Reveal effect="animated fadeIn" duration={1200}>
              <h2 className="casual-fridays-heading casual-fridays-heading--center">How it works</h2>
            </Reveal>

            {steps.map((step, i) => (
              <div key={step.num} className="casual-fridays-step-row">
                <Reveal effect="animated fadeInLeft" duration={1000}>
                  <div className="casual-fridays-step-content">
                    <div className="casual-fridays-step-heading">
                      <span className="casual-fridays-step-num">{step.num}</span>
                      <h3>{step.title}</h3>
                    </div>
                    <p>{step.text}</p>
                  </div>
                </Reveal>
              </div>
            ))}

            {SHOW_REGISTER && (
              <Reveal effect="animated fadeIn" duration={1200}>
                <div className="casual-fridays-cta-wrap">
                  <a href="#signup" className="casual-fridays-cta">Register now</a>
                </div>
              </Reveal>
            )}
          </div>
        </section>

        {/* Watch the show — single full-width image + copy */}
        <section className="casual-fridays-section casual-fridays-section--watch">
          <div className="casual-fridays-section-inner casual-fridays-section-inner--wide">
            <Reveal effect="animated fadeIn" delay={150} duration={1200}>
              <div className="casual-fridays-watch-media casual-fridays-watch-media--horizontal" style={{ aspectRatio: '16/9' }}>
                <img src={cf3} alt="Sustainable style at the festival" />
              </div>
            </Reveal>
            <Reveal effect="animated fadeIn" delay={300} duration={1200}>
              <p className="casual-fridays-watch-text">
                Watch the show, learn simple ways to style sustainably, and join Short Talks on low-impact fashion.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Airtable signup form — replace src with your Airtable embed URL */}
        {SHOW_REGISTER && (
        <section id="signup" className="casual-fridays-section casual-fridays-section--form">
          <div className="casual-fridays-section-inner casual-fridays-section-inner--wide">
            <Reveal effect="animated fadeIn" duration={1200}>
              <h2 className="casual-fridays-heading casual-fridays-heading--center">Register</h2>
              <p className="casual-fridays-form-intro">
                Register your outfit and tell your story by April 15th for Weekend 2.
              </p>
              <p className="casual-fridays-rules-link">
                <a href="/casual-fridays-rules" target="_blank" rel="noopener noreferrer">
                  Official contest rules
                </a>
              </p>
            </Reveal>
            {/* Share this page — below Register your outfit line */}
            <Reveal effect="animated fadeIn" duration={1200}>
              <p className="casual-fridays-share-label">Share this page</p>
              <div className="casual-fridays-share-buttons">
                <a
                  className="casual-fridays-share-btn"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${SHARE_URL_ENCODED}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
                <a
                  className="casual-fridays-share-btn"
                  href={`https://twitter.com/intent/tweet?url=${SHARE_URL_ENCODED}&text=${SHARE_TEXT_ENCODED}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  X (Twitter)
                </a>
                <a
                  className="casual-fridays-share-btn"
                  href={`https://wa.me/?text=${SHARE_TEXT_ENCODED}%20${SHARE_URL_ENCODED}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
                <button
                  type="button"
                  className="casual-fridays-share-btn casual-fridays-share-btn--copy"
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                >
                  {copied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            </Reveal>
            <div className="casual-fridays-airtable-wrap">
              <iframe
                className="casual-fridays-airtable-embed"
                title="Casual Fridays registration"
                src="https://airtable.com/embed/app2eBnuOwIImOlZ6/shrjZisM0j1TnORyg"
                frameBorder="0"
                width="100%"
                height="533"
              />
            </div>
          </div>
        </section>
        )}
      </div>
    </Layout>
  );
};

export default CasualFridays;
